/**
 * OrdersAdminPanel — staff (manager/admin/owner) management of today's online
 * orders, right on the order page.
 *
 *   • Live list of today's online orders with paid / pay-at-pickup badges and
 *     a pickup status flow: New → In progress → Ready → Picked up.
 *   • Inline item editing (quantity steppers + remove) for UNPAID orders only —
 *     paid orders keep honest totals and are canceled & refunded instead.
 *   • Cancel with one confirm; paid orders are refunded in full automatically.
 *
 * Everything writes straight to Square (version-safe sparse updates), so the
 * POS, KDS, and dashboard all stay in sync — this panel is just another
 * window onto Square's order, never a parallel copy.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { auth } from '../../../firebase';
import { useOrdering } from './OrderingContext';
import { adminListOrders, adminOrderAction, MONEY } from './orderApi';
import { fmtPhone, telHref } from '../../../utils/formatUtils';

const FLOW = [
  { state: 'PROPOSED',  label: 'New',         next: 'RESERVED',  nextLabel: 'Start' },
  { state: 'RESERVED',  label: 'In progress', next: 'PREPARED',  nextLabel: 'Ready' },
  { state: 'PREPARED',  label: 'Ready',       next: 'COMPLETED', nextLabel: 'Picked up' },
  { state: 'COMPLETED', label: 'Picked up',   next: null },
  { state: 'CANCELED',  label: 'Canceled',    next: null },
];
const flowOf = (s) => FLOW.find(f => f.state === s) || FLOW[0];

const timeOf = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

function StatusChip({ order }) {
  if (order.state === 'CANCELED') return <span className="oap-chip oap-chip--gray">Canceled</span>;
  const f = flowOf(order.fulfillment.state);
  const cls = { PROPOSED: 'oap-chip--new', RESERVED: 'oap-chip--work', PREPARED: 'oap-chip--ready', COMPLETED: 'oap-chip--done' }[f.state] || 'oap-chip--new';
  return <span className={`oap-chip ${cls}`}>{f.label}</span>;
}

function OrderCard({ order, busy, onAction, onSaveEdit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);          // [{uid, name, qty, removed}]
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(fmtPhone(order.pickupPhone));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard unavailable — the tel: link still works */ }
  };

  const startEdit = () => {
    setDraft(order.items.map(it => ({ uid: it.uid, name: it.name, qty: it.qty, removed: false })));
    setEditing(true);
  };
  const stopEdit = () => { setEditing(false); setDraft(null); };

  const setQty = (uid, qty) => setDraft(d => d.map(x => x.uid === uid ? { ...x, qty: Math.max(1, Math.min(99, qty)) } : x));
  const toggleRemove = (uid) => setDraft(d => d.map(x => x.uid === uid ? { ...x, removed: !x.removed } : x));

  const saveEdit = async () => {
    const updates = draft.filter(x => !x.removed && x.qty !== order.items.find(i => i.uid === x.uid)?.qty)
      .map(x => ({ uid: x.uid, qty: x.qty }));
    const removes = draft.filter(x => x.removed).map(x => x.uid);
    if (!updates.length && !removes.length) { stopEdit(); return; }
    if (draft.every(x => x.removed)) return; // guard mirrored server-side
    const ok = await onSaveEdit(order.id, { updates, removes });
    if (ok) stopEdit();
  };

  const active = order.state === 'OPEN';
  const f = flowOf(order.fulfillment.state);
  const canEdit = active && !order.paid && f.state !== 'COMPLETED';

  return (
    <div className={`oap-card${order.state === 'CANCELED' ? ' oap-card--off' : ''}`}>
      <div className="oap-card__head">
        <div>
          <strong className="oap-card__name">{order.pickupName || 'Online order'}</strong>
          <span className="oap-card__meta">#{order.shortId} · {timeOf(order.createdAt)}</span>
        </div>
        <div className="oap-card__chips">
          {order.paid
            ? <span className="oap-chip oap-chip--paid">PAID</span>
            : order.payAtStore
              ? <span className="oap-chip oap-chip--due">PAY AT PICKUP</span>
              : <span className="oap-chip oap-chip--gray">UNPAID</span>}
          <StatusChip order={order} />
        </div>
      </div>

      {order.pickupPhone ? (
        <div className="oap-phone">
          <a className="oap-phone__link" href={telHref(order.pickupPhone)}>
            <span aria-hidden="true">📞</span> {fmtPhone(order.pickupPhone)}
          </a>
          <button type="button" className="oap-phone__copy" onClick={copyPhone} title="Copy phone number">
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      ) : (
        <div className="oap-phone oap-phone--missing">⚠ No phone on file — ask at pickup</div>
      )}
      {order.note && <div className="oap-card__note">📝 {order.note}</div>}

      <div className="oap-items">
        {(editing ? draft : order.items).map(it => (
          <div key={it.uid} className={`oap-item${editing && it.removed ? ' oap-item--removed' : ''}`}>
            {editing ? (
              <>
                <div className="ord-qty">
                  <button onClick={() => setQty(it.uid, it.qty - 1)} disabled={it.removed} aria-label="Decrease">−</button>
                  <span>{it.qty}</span>
                  <button onClick={() => setQty(it.uid, it.qty + 1)} disabled={it.removed} aria-label="Increase">+</button>
                </div>
                <span className="oap-item__name">{it.name}</span>
                <button className="oap-item__rm" onClick={() => toggleRemove(it.uid)}>
                  {it.removed ? 'Undo' : '🗑'}
                </button>
              </>
            ) : (
              <>
                <span className="oap-item__qty">{it.qty}×</span>
                <span className="oap-item__name">
                  {it.name}{it.variationName && it.variationName !== 'Regular' ? ` (${it.variationName})` : ''}
                  {it.modifiers.length > 0 && <small> · {it.modifiers.join(', ')}</small>}
                  {it.note && <small> · “{it.note}”</small>}
                </span>
                <span className="oap-item__price">{MONEY(it.totalCents)}</span>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="oap-card__foot">
        <strong className="oap-card__total">{MONEY(order.totalCents)}</strong>
        <div className="oap-card__actions">
          {editing ? (
            <>
              <button className="ord-btn ord-btn--ghost oap-btn" onClick={stopEdit} disabled={busy}>Discard</button>
              <button className="ord-btn ord-btn--primary oap-btn" onClick={saveEdit} disabled={busy}>
                {busy ? 'Saving…' : 'Save changes'}
              </button>
            </>
          ) : (
            <>
              {canEdit && (
                <button className="ord-btn ord-btn--ghost oap-btn" onClick={startEdit} disabled={busy}>✏️ Edit</button>
              )}
              {active && (confirmCancel ? (
                <button
                  className="oap-btn oap-btn--danger"
                  onClick={() => { setConfirmCancel(false); onAction(order.id, { action: 'cancel' }); }}
                  onBlur={() => setConfirmCancel(false)}
                  disabled={busy}>
                  {order.paid ? 'Confirm cancel + refund' : 'Confirm cancel'}
                </button>
              ) : (
                <button className="oap-btn oap-btn--dangerghost" onClick={() => setConfirmCancel(true)} disabled={busy}>
                  {order.paid ? 'Cancel & refund' : 'Cancel'}
                </button>
              ))}
              {active && f.next && (
                <button
                  className="ord-btn ord-btn--primary oap-btn"
                  onClick={() => onAction(order.id, { action: 'fulfillment', state: f.next })}
                  disabled={busy}>
                  {busy ? '…' : `${f.nextLabel} →`}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OrdersAdminPanel() {
  const { isStaff, ordersOpen, setOrdersOpen } = useOrdering();
  const [orders, setOrders] = useState(null); // null = loading
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [flash, setFlash] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error('Sign in again to manage orders.');
      setOrders(await adminListOrders(idToken));
    } catch (e) {
      setErr(e.message || 'Could not load orders');
      setOrders([]);
    }
  }, []);

  useEffect(() => {
    if (!ordersOpen) return undefined;
    setOrders(null);
    load();
    const t = setInterval(load, 30000); // keep the truck view fresh
    return () => clearInterval(t);
  }, [ordersOpen, load]);

  if (!isStaff || !ordersOpen) return null;

  const act = async (orderId, payload) => {
    setBusyId(orderId); setErr(''); setFlash('');
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await adminOrderAction({ orderId, ...payload }, idToken);
      if (payload.action === 'cancel' && res.refunds?.length) {
        setFlash(`Canceled — refunded ${MONEY(res.refunds.reduce((s, r) => s + r.amount, 0))} (${res.refunds[0].status.toLowerCase()}).`);
        if (res.warning) setErr(res.warning);
      }
      await load();
      return true;
    } catch (e) {
      setErr(e.message || 'Update failed');
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const saveEdit = (orderId, changes) => act(orderId, { action: 'edit-items', ...changes });

  const activeOrders = (orders || []).filter(o => o.state === 'OPEN');
  const doneOrders = (orders || []).filter(o => o.state !== 'OPEN');

  return (
    <div className="ord-overlay" onClick={() => setOrdersOpen(false)}>
      <div className="ord-modal oap-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Manage orders">
        <div className="ord-drawer__head">
          <h3>Today's online orders</h3>
          <div className="oap-head__btns">
            <button className="osc-refresh" onClick={load} title="Refresh" aria-label="Refresh orders">↻</button>
            <button className="ord-x" onClick={() => setOrdersOpen(false)} aria-label="Close">✕</button>
          </div>
        </div>

        <div className="ord-modal__body">
          {err && <div className="ord-err">⚠ {err}</div>}
          {flash && <div className="osc-notice">{flash}</div>}

          {orders === null ? (
            <div className="osc-state"><div className="osc-spin" /><p>Loading orders…</p></div>
          ) : orders.length === 0 ? (
            <div className="ord-empty"><span>🧾</span><p>No online orders yet today.</p></div>
          ) : (
            <>
              {activeOrders.map(o => (
                <OrderCard key={o.id} order={o} busy={busyId === o.id} onAction={act} onSaveEdit={saveEdit} />
              ))}
              {doneOrders.length > 0 && (
                <>
                  <div className="oap-divider">Done / canceled</div>
                  {doneOrders.map(o => (
                    <OrderCard key={o.id} order={o} busy={busyId === o.id} onAction={act} onSaveEdit={saveEdit} />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
